import {
  Brush as BrushIcon,
  CloudDone as CloudIcon,
  Diamond as DiamondIcon,
  FlashOn as FlashIcon,
  Psychology as BrainIcon,
  Rocket as RocketIcon,
  Security as SecurityIcon,
  TrendingUp as TrendingIcon,
  Visibility as VisibilityIcon
} from '@mui/icons-material';
import {
  Box,
  Button,
  Container,
  Grid,
  Paper,
  Stack,
  Typography
} from '@mui/material';
import { useEffect, useRef, useState } from 'react';

// Import images
import featuresImage from '../assets/flow.webp';

const FeaturesRevolution = () => {
  const containerRef = useRef(null);
  const [visibleCards, setVisibleCards] = useState(new Set());
  const [mousePosition, setMousePosition] = useState({ x: 50, y: 50 });
  const [activeFeature, setActiveFeature] = useState(0);

  // Modern feature data
  const features = [
    {
      id: 1,
      icon: <BrainIcon />,
      title: "AI Destekli İçerik",
      subtitle: "Yapay Zeka ile Hızlı Üretim",
      description: "Gelişmiş AI teknolojisi ile içeriklerinizi otomatik optimize edin. Başlık önerileri, SEO iyileştirmeleri ve otomatik etiketleme.",
      details: [
        "Otomatik başlık önerileri",
        "SEO skorlama sistemi",
        "Akıllı etiketleme",
        "İçerik analizi"
      ],
      color: "#6366f1",
      gradient: "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)",
      image: featuresImage
    },
    {
      id: 2,
      icon: <FlashIcon />,
      title: "Işık Hızında Performans",
      subtitle: "Milisaniyelerle Ölçülen Hız",
      description: "Next.js ve modern teknolojiler ile optimize edilmiş performans. Sayfa yükleme süreleri 300ms altında.",
      details: [
        "300ms altı yükleme",
        "CDN entegrasyonu",
        "Otomatik optimizasyon",
        "Lazy loading"
      ],
      color: "#f59e0b",
      gradient: "linear-gradient(135deg, #f59e0b 0%, #ef4444 100%)",
      image: featuresImage
    },
    {
      id: 3,
      icon: <SecurityIcon />,
      title: "Kurumsal Güvenlik",
      subtitle: "Bank Seviyesinde Koruma",
      description: "SSL şifreleme, çok faktörlü kimlik doğrulama ve gelişmiş güvenlik duvarı ile verileriniz tamamen güvende.",
      details: [
        "SSL şifreleme",
        "2FA güvenlik",
        "Güvenlik duvarı",
        "Otomatik yedekleme"
      ],
      color: "#10b981",
      gradient: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
      image: featuresImage
    },
    {
      id: 4,
      icon: <BrushIcon />,
      title: "Sınırsız Özelleştirme",
      subtitle: "Hayal Ettiğiniz Tasarım",
      description: "Drag & drop editör ile kod bilmeden profesyonel tasarımlar. 1000+ şablon ve sınırsız özelleştirme seçeneği.",
      details: [
        "Drag & drop editör",
        "1000+ şablon",
        "Renk paleti",
        "Font kütüphanesi"
      ],
      color: "#8b5cf6",
      gradient: "linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)",
      image: featuresImage
    },
    {
      id: 5,
      icon: <TrendingIcon />,
      title: "Gelişmiş Analitik",
      subtitle: "Veriler Konuşuyor",
      description: "Gerçek zamanlı analitik raporları ile performansınızı takip edin. Ziyaretçi davranışları ve içerik başarı metrikleri.",
      details: [
        "Gerçek zamanlı raporlar",
        "Kullanıcı davranışları",
        "İçerik metrikleri",
        "SEO performansı"
      ],
      color: "#ef4444",
      gradient: "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
      image: featuresImage
    },
    {
      id: 6,
      icon: <CloudIcon />,
      title: "Bulut Teknolojisi",
      subtitle: "Her Yerden Erişim",
      description: "Bulut tabanlı altyapı ile her cihazdan erişim. Otomatik yedekleme ve senkronizasyon ile verileriniz her zaman güvende.",
      details: [
        "Bulut depolama",
        "Otomatik senkronizasyon",
        "Çoklu cihaz desteği",
        "Offline çalışma"
      ],
      color: "#06b6d4",
      gradient: "linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)",
      image: featuresImage
    }
  ];

  // Mouse tracking
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        setMousePosition({
          x: ((e.clientX - rect.left) / rect.width) * 100,
          y: ((e.clientY - rect.top) / rect.height) * 100
        });
      }
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  // Auto feature rotation
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveFeature((prev) => (prev + 1) % features.length);
    }, 4000);

    return () => clearInterval(interval);
  }, [features.length]);

  // Intersection observer for animations
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const index = parseInt(entry.target.dataset.index);
            setVisibleCards(prev => new Set([...prev, index]));
          }
        });
      },
      { threshold: 0.3 }
    );

    const cards = document.querySelectorAll('[data-index]');
    cards.forEach(card => observer.observe(card));

    return () => observer.disconnect();
  }, []);

  const currentFeature = features[activeFeature];

  return (
    <Box
      ref={containerRef}
      sx={{
        position: 'relative',
        py: { xs: 8, md: 12 },
        background: `
          linear-gradient(
            135deg,
            #ffffff 0%,
            #f8fafc 25%,
            #f1f5f9 50%,
            #e2e8f0 75%,
            #cbd5e1 100%
          )
        `,
        color: '#1e293b',
        overflow: 'hidden'
      }}
    >
      {/* Background Effects */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `
            radial-gradient(
              circle at ${mousePosition.x}% ${mousePosition.y}%,
              ${currentFeature.color}08 0%,
              transparent 50%
            )
          `,
          pointerEvents: 'none',
          transition: 'background 0.6s ease'
        }}
      />

      {/* Floating Elements */}
      <Box
        sx={{
          position: 'absolute',
          top: '20%',
          right: '10%',
          width: 200,
          height: 200,
          background: `${currentFeature.color}10`,
          borderRadius: '50%',
          filter: 'blur(40px)',
          animation: 'floatSlow 8s ease-in-out infinite',
          transition: 'background 0.8s ease'
        }}
      />

      <Box
        sx={{
          position: 'absolute',
          bottom: '30%',
          left: '8%',
          width: 150,
          height: 150,
          background: `${currentFeature.color}08`,
          borderRadius: '50%',
          filter: 'blur(30px)',
          animation: 'floatSlow 6s ease-in-out infinite reverse',
          transition: 'background 0.8s ease'
        }}
      />

      <Container maxWidth="xl">
        {/* Header Section */}
        <Box sx={{ textAlign: 'center', mb: 10 }}>

          <Typography
            variant="h2"
            sx={{
              fontSize: { xs: '2.5rem', md: '3.5rem', lg: '4.5rem' },
              fontWeight: 900,
              mb: 3,
              fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
              background: `linear-gradient(135deg, #1e293b 0%, ${currentFeature.color} 50%, #1e293b 100%)`,
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              // animation: 'headerTitleGlow 3s ease-in-out infinite',
              transition: 'background 0.8s ease',
              position: 'relative',
              '&::after': {
                content: '""',
                position: 'absolute',
                bottom: -12,
                left: '50%',
                transform: 'translateX(-50%)',
                width: 120,
                height: 6,
                background: `linear-gradient(135deg, #1e293b 0%, ${currentFeature.color} 50%, #1e293b 100%)`,
                borderRadius: 3,
                //animation: 'shimmer 5s ease-in-out infinite'
              }
            }}
          >
            Güçlü Özellikler
          </Typography>

          <Typography
            variant="h5"
            sx={{
              fontSize: { xs: '1.2rem', md: '1.4rem' },
              color: '#64748b',
              maxWidth: 600,
              mx: 'auto',
              lineHeight: 1.6,
              animation: 'headerSubtitleFade 1s ease-out 0.3s both'
            }}
          >
            Modern teknolojiler ve yenilikçi çözümlerle projelerinizi bir üst seviyeye taşıyın
          </Typography>
        </Box>

        {/* Main Features Grid */}
        <Grid container spacing={4} sx={{ mb: 12 }}>
          {features.map((feature, index) => (
            <Grid item xs={12} md={6} lg={4} key={feature.id}>
              <Paper
                data-index={index}
                elevation={0}
                sx={{
                  p: 4,
                  height: '100%',
                  background: `linear-gradient(135deg, ${feature.color}08, rgba(255,255,255,0.9))`,
                  backdropFilter: 'blur(20px)',
                  border: `1px solid ${feature.color}20`,
                  borderRadius: 4,
                  position: 'relative',
                  overflow: 'hidden',
                  cursor: 'pointer',
                  opacity: visibleCards.has(index) ? 1 : 0,
                  transform: visibleCards.has(index) ? 'translateY(0)' : 'translateY(40px)',
                  transition: 'all 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                  transitionDelay: `${index * 0.1}s`,
                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: '-100%',
                    width: '100%',
                    height: '100%',
                    background: `linear-gradient(90deg, transparent, ${feature.color}15, transparent)`,
                    transition: 'left 0.8s ease'
                  },
                  '&:hover': {
                    transform: 'translateY(-10px) scale(1.02)',
                    borderColor: `${feature.color}40`,
                    boxShadow: `0 20px 40px ${feature.color}25`,
                    '&::before': {
                      left: '100%'
                    }
                  }
                }}
                onMouseEnter={() => setActiveFeature(index)}
              >
                {/* Feature Icon */}
                <Box
                  sx={{
                    display: 'inline-block',
                    mb: 3,
                    p: 2,
                    background: `${feature.color}15`,
                    borderRadius: '50%',
                    border: `2px solid ${feature.color}30`
                  }}
                >
                  <Box
                    sx={{
                      color: feature.color,
                      fontSize: '2.5rem'
                    }}
                  >
                    {feature.icon}
                  </Box>
                </Box>

                {/* Feature Content */}
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 700,
                    mb: 2,
                    color: '#1e293b',
                    fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif'
                  }}
                >
                  {feature.title}
                </Typography>

                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 500,
                    mb: 3,
                    color: feature.color,
                    fontSize: '1.1rem'
                  }}
                >
                  {feature.subtitle}
                </Typography>

                <Typography
                  variant="body1"
                  sx={{
                    color: '#64748b',
                    lineHeight: 1.7,
                    mb: 4
                  }}
                >
                  {feature.description}
                </Typography>

                {/* Feature Details */}
                <Stack spacing={1.5} sx={{ mb: 4 }}>
                  {feature.details.map((detail, idx) => (
                    <Box
                      key={idx}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5
                      }}
                    >
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          background: feature.color,
                          boxShadow: `0 0 10px ${feature.color}50`
                        }}
                      />
                      <Typography
                        variant="body2"
                        sx={{
                          color: '#475569',
                          fontWeight: 500
                        }}
                      >
                        {detail}
                      </Typography>
                    </Box>
                  ))}
                </Stack>

                {/* Learn More Button */}
                <Button
                  variant="outlined"
                  sx={{
                    borderColor: `${feature.color}60`,
                    color: feature.color,
                    fontWeight: 600,
                    px: 3,
                    py: 1.5,
                    borderRadius: 3,
                    textTransform: 'none',
                    '&:hover': {
                      borderColor: feature.color,
                      bgcolor: `${feature.color}10`,
                      transform: 'scale(1.05)'
                    },
                    transition: 'all 0.3s ease'
                  }}
                >
                  Detayları Gör
                </Button>
              </Paper>
            </Grid>
          ))}
        </Grid>

        {/* Call to Action */}
        <Box
          sx={{
            textAlign: 'center',
            p: 6,
            background: `linear-gradient(135deg, ${currentFeature.color}08, rgba(255,255,255,0.95))`,
            backdropFilter: 'blur(20px)',
            border: `1px solid ${currentFeature.color}20`,
            borderRadius: 6,
            position: 'relative',
            overflow: 'hidden',
            transition: 'all 0.8s ease',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)'
          }}
        >
          <Typography
            variant="h3"
            sx={{
              fontSize: { xs: '2rem', md: '2.5rem' },
              fontWeight: 800,
              mb: 3,
              color: '#1e293b'
            }}
          >
            Başlamaya Hazır mısınız?
          </Typography>

          <Typography
            variant="h6"
            sx={{
              color: '#64748b',
              mb: 4,
              maxWidth: 500,
              mx: 'auto'
            }}
          >
            Tüm bu güçlü özellikleri hemen deneyimlemeye başlayın
          </Typography>

          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={3}
            sx={{ justifyContent: 'center' }}
          >
            <Button
              variant="contained"
              size="large"
              startIcon={<RocketIcon />}
              sx={{
                background: currentFeature.gradient,
                color: 'white',
                fontWeight: 700,
                px: 6,
                py: 2.5,
                fontSize: '1.1rem',
                borderRadius: 50,
                textTransform: 'none',
                boxShadow: `0 15px 35px ${currentFeature.color}40`,
                transition: 'all 0.8s ease',
                '&:hover': {
                  transform: 'translateY(-3px) scale(1.05)',
                  boxShadow: `0 20px 45px ${currentFeature.color}50`
                }
              }}
            >
              Ücretsiz Deneyin
            </Button>

            <Button
              variant="outlined"
              size="large"
              startIcon={<VisibilityIcon />}
              sx={{
                borderColor: `${currentFeature.color}60`,
                color: currentFeature.color,
                fontWeight: 600,
                px: 6,
                py: 2.5,
                fontSize: '1.1rem',
                borderRadius: 50,
                textTransform: 'none',
                backdropFilter: 'blur(10px)',
                '&:hover': {
                  borderColor: currentFeature.color,
                  bgcolor: `${currentFeature.color}10`,
                  transform: 'translateY(-3px) scale(1.05)'
                },
                transition: 'all 0.3s ease'
              }}
            >
              Demo İzle
            </Button>
          </Stack>
        </Box>
      </Container>

      {/* CSS Animations */}
      <style jsx global>{`
        @keyframes floatSlow {
          0%, 100% { transform: translateY(0px) scale(1); }
          50% { transform: translateY(-30px) scale(1.1); }
        }

        @keyframes headerIconFloat {
          0%, 100% { transform: translateY(0px) rotate(0deg) scale(1); }
          50% { transform: translateY(-10px) rotate(5deg) scale(1.05); }
        }

        @keyframes headerTitleGlow {
          0%, 100% { text-shadow: 0 0 40px rgba(30, 41, 59, 0.3); }
          50% { text-shadow: 0 0 60px rgba(30, 41, 59, 0.5), 0 0 20px currentColor; }
        }

        @keyframes headerSubtitleFade {
          0% { transform: translateY(20px); opacity: 0; }
          100% { transform: translateY(0); opacity: 1; }
        }
      `}</style>
    </Box>
  );
};

export default FeaturesRevolution;
